import { useReducer } from 'react';
import axios from 'axios';
import { environmentURLs } from '../../common/CaptureEnvironment';
const SOLUTION_URL = environmentURLs.REACT_APP_API_EMAIL_SOLUTION_SERVICE;
const METRICS_URL = environmentURLs.REACT_APP_METRICS_SERVICE_URL;

const initialState = {
  isloading: false,
  error: null,
  response: null,
  isRequestFulfilled: false
};

function getIntervalFromTimeRange(timeRange) {
  let interval = '1h';
  switch (timeRange) {
    case '10m':
      interval = '1m';
      break;
    case '60m':
      interval = '6m';
      break;
    case '24h':
      interval = '2h';
      break;
    case '7d':
      interval = '1d';
      break;
    case '30d':
      interval = '1d';
      break;
  }
  return interval;
}

const ALL_APIS_OPTION = {
  label: 'All APIs',
  value: 'ALL_APIS'
};

function appendMaxForAppMetrics(metrics) {
  // Calculate max value of transactions to compute thresholds
  // Computing here avoids unnescessary re-renders
  const transactions = metrics.transactions.map(each => {
    let values = each.values.map(each => parseInt(each.value));
    let maxValue = Math.max(...values);
    return {
      ...each,
      maxValue
    };
  });
  const latency = metrics.latency.map(each => {
    let values = each.values.map(each => parseInt(each.value));
    let maxValue = Math.max(...values);
    return {
      ...each,
      maxValue
    };
  });
  const errorRate = metrics.errorRate;
  return {
    transactions,
    latency,
    errorRate
  };
}

function metricsReducer(state, action) {
  switch (action.type) {
    case 'request':
      return {
        ...state,
        isloading: true,
        error: null,
        isRequestFulfilled: false,
        response: null
      };
    case 'receive':
      return {
        ...state,
        isloading: false,
        error: null,
        isRequestFulfilled: true,
        response: action.data
      };
    case 'error':
      return {
        ...state,
        isloading: false,
        error: action.data,
        isRequestFulfilled: true,
        response: null
      };
    default:
      throw new Error();
  }
}


export function useAsyncApiEventsForAppFetch() {
  const [state, dispatch] = useReducer(metricsReducer, initialState);

  function fetchMetrics(appId, accessToken, filters) {
    dispatch({ type: 'request', data: null });
    const apiId =
      filters.apiId === ALL_APIS_OPTION.value ? undefined : filters.apiId;

    const reqBody = {
      select: ['count(messageSize)', 'apiId', 'apiName', 'status'],
      groupBy: ['apiId', 'status'],
      filter: [
        {
          attribute: 'appId',
          operator: 'eq',
          value: [appId]
        },
        {
          attribute: 'operation',
          operator: 'eq',
          value: ['receive']
        }
      ],
      timeRange: filters.timeRange,
      orderBy: ['apiId'],
      persona: 'appPublisher'
    };

    if (!!apiId) {
      reqBody.filter.push({
        attribute: 'apiId',
        operator: 'eq',
        value: [apiId]
      });
    }

    axios({
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      url: `${METRICS_URL}/metrics/events`,
      data: reqBody
    })
      .then(response => {
        const events = [];
        response.data.result.attributes.forEach(data => {
          let event = {};
          data.metrics.forEach(data => {
            event = { ...event, [data.name]: data.value };
          });
          events.push(event);
        });
        dispatch({
          type: 'receive',
          data: events
        });
      })
      .catch(error => {
        dispatch({
          type: 'error',
          data: (error && error.response && error.response.data) || {
            error: 'Failed to fetch Async API events metrics'
          }
        });
      });
  }
  return [
    state.isloading,
    state.isRequestFulfilled,
    state.error,
    state.response,
    fetchMetrics
  ];
}

function appReducer(state, action) {
  switch (action.type) {
    case 'request':
      return { ...state, isloading: true, error: null, response: null };
    case 'receive':
      return { ...state, isloading: false, error: null, response: action.data };
    case 'error':
      return { ...state, isloading: false, error: action.data, response: null };
    default:
      throw new Error();
  }
}

export function useAppFetch() {
  const [state, dispatch] = useReducer(appReducer, initialState);

  function fetchApp(accessToken, appId) {
    dispatch({ type: 'request', data: null });
    axios({
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      url: `${SOLUTION_URL}/v1/solutions/${appId}?returnRetiredAsyncIntegrations=true`
    })
      .then(res => {
        dispatch({ type: 'receive', data: res.data });
      })
      .catch(error => {
        dispatch({
          type: 'error',
          data: (error && error.response && error.response.data) || {
            error: 'Failed to fetch app data'
          }
        });
      });
  }
  return [
    state.isloading,
    state.isRequestFulfilled,
    state.error,
    state.response,
    fetchApp
  ];
}

export function useAppMetricsFetch() {
  const [state, dispatch] = useReducer(metricsReducer, initialState);

  function fetchMetrics(appId, accessToken, filters) {
    dispatch({ type: 'request', data: null });
    axios({
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      params: {
        expand: true,
        timeRange: filters.timeRange,
        interval: getIntervalFromTimeRange(filters.timeRange),
        apiId:
          filters.apiId === ALL_APIS_OPTION.value ? undefined : filters.apiId,
        apiInstanceEnvironment: filters.environment
      },
      url: `${METRICS_URL}/metrics/apps/${appId}`
    })
      .then(res => {
        dispatch({
          type: 'receive',
          data: appendMaxForAppMetrics(res.data)
        });
      })
      .catch(error => {
        dispatch({
          type: 'error',
          data: (error && error.response && error.response.data) || {
            error: 'Failed to fetch app metrics'
          }
        });
      });
  }
  return [
    state.isloading,
    state.isRequestFulfilled,
    state.error,
    state.response,
    fetchMetrics
  ];
}
